import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm } from "react-hook-form";
import { useDispatchApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import useFocusErrorForm from "../../../hooks/useFocusErrorForm";
import {
  ERROR,
  FIELD_REQUIRED,
  PASSWORDS_NOT_MATCH,
  PASSWORD_CONFIRM_PASSWORD_PLACEHOLDER,
  PASSWORD_FORMAT_DESCRIPTION,
  PASSWORD_FORMAT_SUGGESTION1,
  PASSWORD_FORMAT_SUGGESTION2,
  PASSWORD_FORMAT_SUGGESTION3,
  PASSWORD_FORMAT_SUGGESTION4,
  PASSWORD_INSECURE,
  PASSWORD_NEW_PASSWORD_PLACEHOLDER,
  RESET_PASSWORD,
  RESET_PASSWORD_GET_TOKEN_FAILED,
} from "../../../i18n/keysTranslations";
import { REGEX_PASSWORD } from "../../../utils/regex";
import TextField from "../../../components/Form/TextField";
import { startResetPassword } from "../../../actions/auth";
import Button from "../../../components/Buttons/Button";
import { ALERT_ICON_TYPE_ERROR } from "../../../utils/constants";
import { SimpleAlert } from "../../../components/Alerts/Alerts";

const ResetPasswordForm = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatchApp();

  const navigate = useNavigate();

  const [params] = useSearchParams();
  const token = params.get("token");

  const { t } = useTranslationApp();
  const { handleSubmit, watch, control, formState } = useForm({
    defaultValues: {
      newPassword: "",
      repeatPassword: "",
    },
  });

  const errors = formState.errors;
  useFocusErrorForm(formState);

  useEffect(() => {
    if (!token) {
      SimpleAlert({
        title: t(ERROR),
        text: t(RESET_PASSWORD_GET_TOKEN_FAILED),
        icon: ALERT_ICON_TYPE_ERROR,
      });
      navigate("/login");
      return;
    }
    // eslint-disable-next-line
  }, []);

  const onClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data) => {
    setIsFetching(true);
    const response = await dispatch(startResetPassword({ ...data, token }));
    setIsFetching(false);
    if (response) {
      navigate("/login");
    }
  };

  const textFieldSx = {
    borderRadius: 3,
    "& .MuiInputBase-input": {
      minHeight: "10px !important",
    },
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        mt: 2,
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box sx={{ mb: 2, textAlign: "start" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t(PASSWORD_FORMAT_DESCRIPTION)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`* ${t(PASSWORD_FORMAT_SUGGESTION1)}`}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`* ${t(PASSWORD_FORMAT_SUGGESTION2)}`}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`* ${t(PASSWORD_FORMAT_SUGGESTION3)}`}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`* ${t(PASSWORD_FORMAT_SUGGESTION4)}`}
        </Typography>
      </Box>

      <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
        {t(PASSWORD_NEW_PASSWORD_PLACEHOLDER)}
      </Typography>
      <TextField
        margin="dense"
        type={showPassword ? "text" : "password"}
        name="newPassword"
        variant="filled"
        color="primary"
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ ...textFieldSx, mb: 2 }}
        control={control}
        errors={errors}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
          validate: {
            value: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(PASSWORD_INSECURE);
              }
              if (value !== watch("repeatPassword")) {
                return t(PASSWORDS_NOT_MATCH);
              }
            },
          },
        }}
      />

      <Typography variant="h6" color="text.primary" sx={{ fontSize: 14 }}>
        {t(PASSWORD_CONFIRM_PASSWORD_PLACEHOLDER)}
      </Typography>
      <TextField
        margin="dense"
        type={showPassword ? "text" : "password"}
        name="repeatPassword"
        variant="filled"
        color="primary"
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={onClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={textFieldSx}
        control={control}
        errors={errors}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
          validate: {
            value: (value) => {
              if (!REGEX_PASSWORD.test(value)) {
                return t(PASSWORD_INSECURE);
              }
              if (value !== watch("newPassword")) {
                return t(PASSWORDS_NOT_MATCH);
              }
            },
          },
        }}
      />

      <Button
        type="submit"
        color="primary"
        loading={isFetching}
        fullWidth
        variant="contained"
        sx={{
          mt: 2,
        }}
      >
        {t(RESET_PASSWORD)}
      </Button>
    </Box>
  );
};

export default ResetPasswordForm;
